import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../../core/components/Button.tsx';
import DropdownMenu from '../../../../core/components/DropdownMenu.tsx';
import type { ActionType, ActionTypeInfo } from '../types.ts';

const PlusIcon = withIcon(faPlus);

interface AddActionDropdownProps {
    actionTypes: ActionTypeInfo[];
    onAddAction: (actionType: ActionType) => void;
    variant?: 'default' | 'inline' | 'ghost';
    filterOut?: ActionType[];
}

const AddActionDropdown = ({ actionTypes, onAddAction, variant = 'ghost', filterOut }: AddActionDropdownProps) => {
    const { t } = useTranslation('integrations');
    const types = filterOut ? actionTypes.filter((at) => !filterOut.includes(at.value)) : actionTypes;

    if (variant === 'inline') {
        return (
            <DropdownMenu>
                <DropdownMenu.Trigger className="w-full py-1 flex items-center justify-center text-content-weak hover:text-content-strong hover:bg-surface-medium rounded transition-colors group">
                    <div className="w-full h-px bg-line-weak group-hover:bg-line-strong" />
                    <PlusIcon className="w-4 h-4 mx-2 flex-none" />
                    <div className="w-full h-px bg-line-weak group-hover:bg-line-strong" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content className="z-[60000]">
                    {types.map((type) => (
                        <DropdownMenu.Item key={type.value} onSelect={() => onAddAction(type.value)}>
                            <div>
                                <div className="font-medium">{type.label}</div>
                                <div className="text-xs text-content-weak">{type.description}</div>
                            </div>
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu>
        );
    }

    if (variant === 'default') {
        return (
            <DropdownMenu>
                <DropdownMenu.Trigger className="w-full inline-flex items-center justify-center bg-surface-weak text-content-strong rounded px-3 py-1.5 font-medium text-sm border border-solid border-line-strong shadow-sm outline-none cursor-pointer transition-all hover:bg-surface-strong data-[state=open]:bg-surface-brand-medium-default data-[state=open]:text-content-brand-strong data-[state=open]:border-line-brand-default">
                    <PlusIcon className="w-4 h-4 mr-2" />
                    {t('outputMapper.visualEditor.addAction')}
                </DropdownMenu.Trigger>
                <DropdownMenu.Content className="z-[60000]">
                    {types.map((type) => (
                        <DropdownMenu.Item key={type.value} onSelect={() => onAddAction(type.value)}>
                            <div>
                                <div className="font-medium">{type.label}</div>
                                <div className="text-xs text-content-weak">{type.description}</div>
                            </div>
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu>
        );
    }

    return (
        <DropdownMenu modal={false}>
            <DropdownMenu.Trigger asChild>
                <Button
                    variant="ghost"
                    type="button"
                    className="w-full text-sm"
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <PlusIcon className="w-3 h-3 mr-1" />
                    {t('outputMapper.visualEditor.addAction')}
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className="z-[60000]">
                {types.map((type) => (
                    <DropdownMenu.Item key={type.value} onSelect={() => onAddAction(type.value)}>
                        {type.label}
                    </DropdownMenu.Item>
                ))}
            </DropdownMenu.Content>
        </DropdownMenu>
    );
};

export default AddActionDropdown;
